// src/components/ResidentialServices.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../styles/CustomerSiteStyles/ResidentialServices.css'; 

function ResidentialServices() {
  const navigate = useNavigate();
  const handleContactNav = () => {
    navigate('/contact');
  };

  // Services data
  const services = [
    {
      title: 'Shingle Roofing',
      description:
        'We specialize in installing high-quality shingle roofs. Partnered with Owens Corning, we offer a variety of durable and aesthetically pleasing shingles.',
      action: {
        label: 'Design Your Roof',
        href: 'https://roofvisualizer.owenscorning.com/#',
      },
    },
    {
      title: 'Metal Roofing',
      description:
        "Our metal roofing solutions provide longevity and energy efficiency, available in various styles and colors to match your home's design.",
    },
    {
      title: 'Flat Roofing',
      description:
        'We offer expert installation and repair of flat roofing systems, including EPDM, TPO, and Modified Bitumen, ensuring durability and performance.',
    },
    {
      title: 'Siding',
      description:
        "Enhance your home's curb appeal and energy efficiency with our professional siding installation and repair services. We work with various materials to suit your style and budget.",
    },
    {
      title: 'Gutters',
      description:
        'Protect your home from water damage with our gutter installation and maintenance services. We ensure proper drainage to keep your foundation and landscaping safe.',
    },
    {
      title: 'Insulation',
      description:
        "Improve your home's energy efficiency with our insulation services. We help you maintain comfortable temperatures year-round while reducing energy costs.",
    },
    {
      title: 'Painting',
      description:
        'Our professional painting services refresh the look of your home, both interior and exterior. We use high-quality paints and techniques for a flawless finish.',
    },
    {
      title: 'Additional Services',
      description:
        'From roof inspections and maintenance to custom home improvements, we offer a wide range of services to meet all your residential needs.',
    },
  ];

  return (
    <div className="residential-container">
      {/* 1. Hero Section with Parallax Effect */}
      <section className="residential-hero">
        <div className="residential-overlay">
          <div className="residential-content">
            <h1>Expert Residential Services</h1>
            <p>Your home deserves the best. We deliver excellence with every project.</p>
            <button onClick={handleContactNav} className="residential-button">Contact Us</button>
          </div>
        </div>
      </section>

      {/* 2. Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="why-choose-content">
          <h2>Why Choose Affordable Roofing + Solar Solutions?</h2>
          
          <div className="features-grid">
            <div className="feature-item">
              <span className="material-icons">verified</span>
              <h3>Expert Craftsmen</h3>
              <p>Our team consists of trained, certified professionals who take pride in their workmanship.</p>
            </div>
            
            <div className="feature-item">
              <span className="material-icons">payments</span>
              <h3>Insurance Specialists</h3>
              <p>We maximize your insurance claims to minimize your out-of-pocket expenses.</p>
            </div>
            
            <div className="feature-item">
              <span className="material-icons">inventory_2</span>
              <h3>Premium Materials</h3>
              <p>We partner with <a href="https://www.owenscorning.com/" target="_blank" rel="noopener noreferrer" className="partner-link">Owens Corning</a> and other industry leaders to provide the highest quality materials for your home.</p>
            </div>
            
            <div className="feature-item">
              <span className="material-icons">handshake</span>
              <h3>Customer Satisfaction</h3>
              <p>Our transparent process and exceptional communication ensure your complete satisfaction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Insurance Claim Highlight Section */}
      <section className="insurance-highlight">
        <div className="insurance-content">
          <div className="insurance-text-container">
            <h2>We Work With Your Insurance</h2>
            <div className="insurance-details">
              <div className="insurance-text">
                <p>Storm damage? We handle the entire insurance claim process for you, minimizing your out-of-pocket costs and streamlining repairs. Our experts will assess damage, document everything, and work directly with your insurance company.</p>
                <ul className="insurance-benefits">
                  <li><span className="material-icons">check_circle</span>Free comprehensive damage assessment</li>
                  <li><span className="material-icons">check_circle</span>Direct insurance company coordination</li>
                  <li><span className="material-icons">check_circle</span>Detailed documentation and photos</li>
                  <li><span className="material-icons">check_circle</span>Claim filing assistance</li>
                </ul>
                <button onClick={handleContactNav} className="insurance-button">Learn About Our Process</button>
              </div>
              <div className="insurance-icon-container">
                <div className="insurance-icon">
                  <span className="material-icons">shield</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Services List Section */}
      <section className="residential-services-section">
        <div className="residential-services-header">
          <h2>Our Residential Services</h2>
          <p>Comprehensive solutions for every part of your home</p>
        </div>
        
        <div className="residential-services-list">
          {services.map((service, index) => (
            <ServiceListItem key={index} service={service} />
          ))}
        </div>
      </section>

      {/* 5. Call-to-Action Section */}
      <section className="residential-cta-section">
        <div className="residential-cta-content">
          <h2>Ready to Transform Your Home?</h2>
          <p>Contact us today for a free, no-obligation consultation.</p>
          <button onClick={handleContactNav} className="residential-cta-button">Contact Our Team</button>
        </div>
      </section>
    </div>
  );
}

// Service List Item Component
function ServiceListItem({ service }) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="service-list-item">
      <div className="service-list-header" onClick={toggleExpand}>
        <div className="service-list-title">
          <h3>{service.title}</h3>
        </div>
        <span className="material-icons toggle-icon">
          {expanded ? 'remove' : 'add'}
        </span>
      </div>
      
      {expanded && (
        <div className="service-list-content">
          <p>{service.description}</p>
          {service.action && (
            <a
              className="service-list-action"
              href={service.action.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {service.action.label}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default ResidentialServices;